import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Target, PencilSimple, Trash, ArrowClockwise, Play, Pause, Stop,
  ArrowRight, DotsThreeVertical, Warning, Lightning, UsersThree, Wallet,
  CalendarBlank, Spinner, XCircle, CheckCircle, X,
} from '@phosphor-icons/react';
import LogisticsLayout, { formatMinor } from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import { useAuth } from '../../context/AuthContext';
import CreateExerciseDrawer from '../../components/logistics/CreateExerciseDrawer';
import EditExerciseDrawer from '../../components/logistics/EditExerciseDrawer';
import logisticsApi from '../../lib/logisticsApi';

const SYM = 'Ÿ';
const ADMIN_ROLES = ['super_admin', 'hub_admin'];

const STATUS_COLORS = {
  draft: { bg: 'var(--ls-elevated)', color: 'var(--ls-muted)' },
  active: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  paused: { bg: 'rgba(234,179,8,0.15)', color: '#eab308' },
  ended: { bg: 'var(--ls-elevated)', color: 'var(--ls-muted)' },
  archived: { bg: 'var(--ls-elevated)', color: 'var(--ls-muted)' },
};

/* ======== Confirmation Modal ======== */
const ConfirmModal = ({ open, onClose, onConfirm, title, description, danger, confirmLabel, exerciseName }) => {
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);
  const requiresTyping = danger;
  const canConfirm = requiresTyping ? typed === exerciseName : true;

  useEffect(() => { if (!open) { setTyped(''); setLoading(false); } }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try { await onConfirm(); } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="ls-surface p-6 max-w-md w-full mx-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="fill" style={{ color: danger ? 'var(--ls-danger)' : 'var(--ls-primary)' }} />
            <h3 className="ls-heading text-lg font-bold">{title}</h3>
          </div>
          <button onClick={onClose} className="ls-btn-ghost !p-1.5"><X size={16} /></button>
        </div>
        <p className="text-sm mb-4" style={{ color: 'var(--ls-muted)' }}>{description}</p>
        {requiresTyping && (
          <div className="mb-4">
            <p className="text-xs mb-2" style={{ color: 'var(--ls-secondary)' }}>
              Type <strong style={{ color: 'var(--ls-danger)' }}>{exerciseName}</strong> to confirm:
            </p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="ls-input w-full ls-mono text-sm"
              placeholder={exerciseName}
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="ls-btn-ghost text-sm">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
            className="text-sm px-4 py-2 rounded-sm font-medium transition-colors flex items-center gap-1.5"
            style={{
              background: danger ? 'var(--ls-danger)' : 'var(--ls-primary)',
              color: '#fff',
              opacity: (!canConfirm || loading) ? 0.5 : 1,
            }}
          >
            {loading ? <Spinner size={14} className="animate-spin" /> : null}
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ======== Actions Menu ======== */
const ActionsMenu = ({ exercise, onEdit, onStatusChange, onReset, onDelete }) => {
  const [open, setOpen] = useState(false);
  const status = exercise.status;

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="ls-btn-ghost !p-1.5"
        title="More actions"
      >
        <DotsThreeVertical size={16} weight="bold" />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.14 }}
              className="ls-surface absolute right-0 mt-1 min-w-[180px] py-1.5 z-50"
              style={{ top: '100%' }}
            >
              <button
                onClick={() => { setOpen(false); onEdit(); }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
              >
                <PencilSimple size={14} /> Edit
              </button>
              {(status === 'draft' || status === 'paused') && (
                <button
                  onClick={() => { setOpen(false); onStatusChange('activate'); }}
                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
                  style={{ color: '#22c55e' }}
                >
                  <Play size={14} weight="fill" /> Activate
                </button>
              )}
              {status === 'active' && (
                <button
                  onClick={() => { setOpen(false); onStatusChange('pause'); }}
                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
                  style={{ color: '#eab308' }}
                >
                  <Pause size={14} weight="fill" /> Pause
                </button>
              )}
              {(status === 'active' || status === 'paused') && (
                <button
                  onClick={() => { setOpen(false); onStatusChange('end'); }}
                  className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
                >
                  <Stop size={14} weight="fill" /> End Exercise
                </button>
              )}
              <div className="border-t my-1" style={{ borderColor: 'var(--ls-border)' }} />
              <button
                onClick={() => { setOpen(false); onReset(); }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
                style={{ color: '#eab308' }}
              >
                <ArrowClockwise size={14} /> Reset Exercise
              </button>
              <button
                onClick={() => { setOpen(false); onDelete(); }}
                className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--ls-accent)] transition-colors"
                style={{ color: 'var(--ls-danger)' }}
              >
                <Trash size={14} /> Delete Permanently
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ======== Exercise Card ======== */
const ExerciseCard = ({ exercise, onEnter, onEdit, onStatusChange, onReset, onDelete, index }) => {
  const sc = STATUS_COLORS[exercise.status] || STATUS_COLORS.draft;
  const startDate = exercise.start_at ? new Date(exercise.start_at).toLocaleDateString() : null;
  const endDate = exercise.end_at ? new Date(exercise.end_at).toLocaleDateString() : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="ls-surface ls-noise p-5 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Target size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
            <h3 className="ls-heading text-lg font-bold truncate">{exercise.name}</h3>
          </div>
          {exercise.company && (
            <p className="text-xs truncate" style={{ color: 'var(--ls-muted)' }}>
              {exercise.company}{exercise.sub_company ? ` · ${exercise.sub_company}` : ''}
            </p>
          )}
        </div>
        <span
          className="text-[10px] px-2 py-0.5 rounded-sm font-medium uppercase flex-shrink-0"
          style={{ background: sc.bg, color: sc.color }}
        >
          {exercise.status}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-1.5">
          <UsersThree size={13} style={{ color: 'var(--ls-muted)' }} />
          <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>
            {exercise.member_count || 0} members · {exercise.team_count || 0} teams
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Wallet size={13} style={{ color: 'var(--ls-muted)' }} />
          <span className="text-xs ls-mono" style={{ color: 'var(--ls-muted)' }}>
            {SYM}{formatMinor(exercise.total_allocated || 0)}
          </span>
        </div>
        {(startDate || endDate) && (
          <div className="flex items-center gap-1.5 col-span-2">
            <CalendarBlank size={13} style={{ color: 'var(--ls-muted)' }} />
            <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>
              {startDate || '—'} → {endDate || '—'}
            </span>
          </div>
        )}
      </div>

      {exercise.total_allocated > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-[10px] mb-1" style={{ color: 'var(--ls-muted)' }}>
            <span>Spent {SYM}{formatMinor(exercise.total_spent || 0)}</span>
            <span>{Math.round(((exercise.total_spent || 0) / exercise.total_allocated) * 100)}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--ls-elevated)' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(((exercise.total_spent || 0) / exercise.total_allocated) * 100, 100)}%`,
                background: 'var(--ls-primary)',
              }}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-auto pt-3 border-t" style={{ borderColor: 'var(--ls-border)' }}>
        <button
          onClick={() => onEnter(exercise)}
          className="ls-btn-primary text-xs flex-1 flex items-center justify-center gap-1.5"
        >
          <ArrowRight size={14} weight="bold" /> Enter
        </button>
        <ActionsMenu
          exercise={exercise}
          onEdit={() => onEdit(exercise)}
          onStatusChange={(action) => onStatusChange(exercise, action)}
          onReset={() => onReset(exercise)}
          onDelete={() => onDelete(exercise)}
        />
      </div>
    </motion.div>
  );
};

/* ======== Main Content ======== */
const ExerciseManagementContent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { exercises, select, refresh, loading } = useLogisticsExercise();
  const isAdmin = ADMIN_ROLES.some(r => (user?.roles || []).includes(r));
  const isSimcel = (user?.roles || []).includes('simcel');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Auto-redirect participants/observers to their assigned exercise
  useEffect(() => {
    if (loading) return;
    if (!isAdmin && !isSimcel && exercises.length > 0) {
      select(exercises[0].logistics_exercise_id);
      navigate('/hub/logistics/home', { replace: true });
    }
  }, [loading, isAdmin, isSimcel, exercises]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEnter = (ex) => {
    select(ex.logistics_exercise_id);
    navigate('/hub/logistics/home');
  };

  const handleEdit = (ex) => {
    setEditingExercise(ex);
    setEditOpen(true);
  };

  const handleStatusChange = async (ex, action) => {
    try {
      if (action === 'activate') await logisticsApi.activateExercise(ex.logistics_exercise_id);
      else if (action === 'pause') await logisticsApi.pauseExercise(ex.logistics_exercise_id);
      else if (action === 'end') await logisticsApi.endExercise(ex.logistics_exercise_id);
      await refresh();
      showToast(`Exercise ${action === 'activate' ? 'activated' : action === 'pause' ? 'paused' : 'ended'}`);
    } catch (e) {
      showToast(e.message || 'Action failed', 'error');
    }
  };

  const handleReset = (ex) => {
    setConfirmAction({
      type: 'reset',
      exercise: ex,
      title: 'Reset Exercise',
      description: 'This will wipe all transactions, bookings, and cards. Wallet balances will be reset to their initial allocation. The exercise status will revert to Draft. This cannot be undone.',
      danger: false,
      confirmLabel: 'Reset Exercise',
    });
  };

  const handleDelete = (ex) => {
    setConfirmAction({
      type: 'delete',
      exercise: ex,
      title: 'Delete Exercise',
      description: 'This will permanently delete the exercise and ALL related data — wallets, transactions, bookings, cards, teams, venues. This cannot be undone.',
      danger: true,
      confirmLabel: 'Delete Permanently',
    });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, exercise } = confirmAction;
    try {
      if (type === 'delete') {
        await logisticsApi.hardDeleteExercise(exercise.logistics_exercise_id);
        showToast(`"${exercise.name}" deleted`);
      } else if (type === 'reset') {
        await logisticsApi.resetExercise(exercise.logistics_exercise_id);
        showToast(`"${exercise.name}" reset to draft`);
      }
      setConfirmAction(null);
      await refresh();
    } catch (e) {
      showToast(e.message || 'Action failed', 'error');
    }
  };

  return (
    <>
      {/* Header */}
      <section className="mb-8">
        <span className="ls-overline flex items-center gap-2 mb-3" style={{ color: 'var(--ls-primary)' }}>
          <Lightning size={12} weight="fill" /> LOGISTICS & SPEND COMMAND
        </span>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="ls-heading text-3xl sm:text-4xl font-bold tracking-tight">Exercise Management</h1>
            <p className="text-sm mt-2 max-w-xl" style={{ color: 'var(--ls-muted)' }}>
              Create, configure, and manage logistics exercises. Each exercise is an isolated workspace
              with its own budget, team, cards, and transaction history.
            </p>
          </div>
          {(isAdmin || isSimcel) && (
            <button
              onClick={() => setCreateOpen(true)}
              className="ls-btn-primary flex items-center gap-1.5 flex-shrink-0"
            >
              <Plus size={16} weight="bold" /> Create Exercise
            </button>
          )}
        </div>
        {exercises.length > 0 && (
          <div className="mt-3">
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'var(--ls-elevated)', color: 'var(--ls-muted)' }}>
              {exercises.length} exercise{exercises.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </section>

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center">
          <Spinner size={20} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
          <p className="text-sm" style={{ color: 'var(--ls-muted)' }}>Loading exercises…</p>
        </div>
      ) : exercises.length === 0 ? (
        <div className="ls-elevated p-12 text-center">
          <Target size={56} weight="duotone" style={{ color: 'var(--ls-primary)' }} className="mx-auto mb-4" />
          <h2 className="ls-heading text-2xl font-bold mb-2">No exercises yet</h2>
          <p className="text-sm max-w-md mx-auto mb-6" style={{ color: 'var(--ls-muted)' }}>
            Create your first logistics exercise to get started. Each exercise comes with
            a team wallet, simulated payment cards, and an immutable transaction ledger.
          </p>
          {(isAdmin || isSimcel) && (
            <button onClick={() => setCreateOpen(true)} className="ls-btn-primary">
              <Plus size={16} weight="bold" /> Create Exercise
            </button>
          )}
        </div>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exercises.map((ex, i) => (
            <ExerciseCard
              key={ex.logistics_exercise_id}
              exercise={ex}
              index={i}
              onEnter={handleEnter}
              onEdit={handleEdit}
              onStatusChange={handleStatusChange}
              onReset={handleReset}
              onDelete={handleDelete}
            />
          ))}
        </section>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-sm shadow-lg"
            style={{
              background: toast.type === 'error' ? 'var(--ls-danger)' : 'var(--ls-success)',
              color: '#fff',
            }}
          >
            {toast.type === 'error' ? <XCircle size={16} weight="fill" /> : <CheckCircle size={16} weight="fill" />}
            <span className="text-sm font-medium">{toast.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm modal */}
      <ConfirmModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeConfirmAction}
        title={confirmAction?.title || ''}
        description={confirmAction?.description || ''}
        danger={confirmAction?.danger}
        confirmLabel={confirmAction?.confirmLabel}
        exerciseName={confirmAction?.exercise?.name || ''}
      />

      {/* Drawers */}
      <CreateExerciseDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { refresh(); setCreateOpen(false); }}
      />
      <EditExerciseDrawer
        open={editOpen}
        exercise={editingExercise}
        onClose={() => { setEditOpen(false); setEditingExercise(null); }}
        onUpdated={() => { refresh(); setEditOpen(false); setEditingExercise(null); }}
      />
    </>
  );
};

const ExerciseManagementPage = () => (
  <LogisticsLayout hideNav>
    <ExerciseManagementContent />
  </LogisticsLayout>
);

export default ExerciseManagementPage;
